import { useState } from 'react';
import { createPortal } from 'react-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useCreateSiblingAhuMutation,
  useGetAhuSiblingListQuery,
} from 'app/services/ahuBoardApi';
import type { IAhuBoardProject, IAllPermissions } from 'models';
import NewAhuPopup from 'pages/project-board/components/ahus/NewAhuPopup';
import { writeReturnProjectBoard } from '../ahuBoardStorage';

interface AhuNameBreadcrumbProps {
  ahuId: number;
  ahuTag: string | undefined;
  project: IAhuBoardProject | undefined;
  permissions: IAllPermissions | undefined;
}

/**
 * #ahu-name — ViewProjectName (§10.1, view/project.name.js +
 * tmpl/project.name.tmpl, markup faithful):
 *  - a.proj-name → /project/<id>#project; click stores
 *    localStorage `returnProjectBoard` = ahu_id (scroll-back target).
 *  - AHU switcher: #ahu-list-show-hide-btn toggles `.focused`; blur closes
 *    unless focus moves into the dropdown (legacy checks
 *    relatedTarget[data-target=ahu_tag]); sibling AHUs from §20.2 #13 open in
 *    new tabs; the current AHU's row is `.hide`.
 *  - "Create New..." gated by project-board `project` tab access AND
 *    project.editable (getPermissions); the click opens the shared new-AHU
 *    popup, whose Create POSTs {project_id, tag, qty: 1}, opens the new AHU in
 *    a tab and refreshes the list (new_ahu.js `fromAhuBoard` branch).
 *  - `update:ahus` refresh (Q7: bot-create) re-lands with the bus consumer in
 *    6e; RTKQ invalidation of 'AhuList' covers the popup-create path.
 *
 * Live tag mirroring from tab.ahu edits (`#ahu-name span.ahu-tag`) becomes a
 * store subscription when the AHU tab lands (6e).
 */
const AhuNameBreadcrumb = ({ ahuId, ahuTag, project, permissions }: AhuNameBreadcrumbProps) => {
  const { data: ahus } = useGetAhuSiblingListQuery(project?.id ?? skipToken);
  const [focused, setFocused] = useState(false);
  const [newAhuOpen, setNewAhuOpen] = useState(false);
  const [createSiblingAhu] = useCreateSiblingAhuMutation();

  const list = ahus ?? [];
  const tag = list.find((a) => a.id === ahuId)?.tag ?? ahuTag ?? '';

  // getPermissions(): project_board_tabs 'project' access && project.editable.
  const createAhuPermission =
    (permissions?.permissions?.project_board_tabs?.find((t) => t.tab === 'project')
      ?.access ?? false) && project?.editable !== false;

  const showList = list.length > 1 || createAhuPermission;
  const hasSiblings = list.length > 1;

  // createNewAHU (project.name.js:createNewAHU): preventDefault, open the
  // shared popup, and drop `.focused` so the dropdown closes behind it.
  const onCreateNewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setNewAhuOpen(true);
    setFocused(false);
  };

  // Popup Create (new_ahu.js:71-83, fromAhuBoard branch): POST, then open the
  // new AHU in a tab and re-render the sibling list (RTKQ 'AhuList' refetch).
  const onCreateAhu = (newTag: string) => {
    if (!project) return;
    createSiblingAhu({ project_id: project.id, tag: newTag })
      .unwrap()
      .then((row) => {
        window.open(`/ahu/detail/${row.id}`, '_blank');
      })
      .catch(() => undefined);
  };

  const popupHost = newAhuOpen ? document.getElementById('new_ahu_popup') : null;

  return (
    <div id="ahu-name" className="left">
      <a
        className="proj-name conected-to-error-log"
        href={project ? `/project/${project.id}#project` : '#'}
        tabIndex={-1}
        onClick={() => writeReturnProjectBoard(ahuId)}
      >
        {project?.name ?? ''}
      </a>
      <i className="icon-ic-keyboard-arrow-right-black-24px" />
      <div
        className={`ahu-list-wrapper conected-to-error-log${showList ? '' : ' with-one-child'}`}
      >
        <button
          id="ahu-list-show-hide-btn"
          className={focused ? 'focused' : ''}
          onClick={() => setFocused((f) => !f)}
          onBlur={(e) => {
            // hideDrpdwn: keep open when focus moves into the dropdown
            // (relatedTarget carries data-target="ahu_tag").
            const related = e.relatedTarget as HTMLElement | null;
            if (related?.getAttribute('data-target') !== 'ahu_tag') setFocused(false);
          }}
        >
          <span className="ahu-tag">{tag}</span>
          <i className="ahu-list-icon icon-ic-keyboard-arrow-right-black-24px" />
        </button>
        <ul className="ahu-list-dropdown" data-target="ahu_tag">
          {hasSiblings && (
            <ul>
              {list.map((a) => (
                <li
                  key={a.id}
                  data-target="ahu_tag"
                  className={a.id === ahuId ? 'hide' : ''}
                >
                  <a
                    target="_blank"
                    rel="noreferrer"
                    data-target="ahu_tag"
                    href={`/ahu/detail/${a.id}`}
                    className={`ahu-tag-${a.id}`}
                    onClick={() => setFocused(false)}
                  >
                    {a.tag}
                  </a>
                </li>
              ))}
            </ul>
          )}
          {createAhuPermission && (
            <li data-target="ahu_tag" className="create_new_ahu_btn_li">
              <button
                data-target="ahu_tag"
                id="create_new_ahu_btn"
                className="plus"
                onClick={onCreateNewClick}
              >
                Create New...
              </button>
            </li>
          )}
        </ul>
      </div>
      {/* Legacy mounts the popup at #new_ahu_popup (new_ahu.js `id`), outside
          #ahu-name — portal to the anchor AhuBoardPage already renders. */}
      {popupHost &&
        createPortal(
          <NewAhuPopup onCreate={onCreateAhu} onClose={() => setNewAhuOpen(false)} />,
          popupHost
        )}
    </div>
  );
};

export default AhuNameBreadcrumb;
